import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Smartphone, MapPin, Clock, ChevronRight } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { fmtHM, fmtDateTime, VISIT_STATUS_LABEL } from "@/components/jobs/jobUtils";
import StatusPill from "@/components/marketplace/StatusPill";

export default function ActiveJobsListPage() {
  const [rows, setRows] = useState(null);
  useDocumentTitle("Active jobs");
  useEffect(() => { api.get("/jobs/active").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  const live = (rows || []).filter((r) => r.visit_status && !["departed", "closed"].includes(r.visit_status));
  const upcoming = (rows || []).filter((r) => !live.includes(r));
  return (
    <DashboardShell>
      <div className="max-w-4xl">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Provider</div>
        <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]" data-testid="active-jobs-title">Active jobs</h1>
        <p className="mt-1 text-sm text-[#252A34]/70">Today's local bookings with the mobile Active Job Screen: on my way, check in, timer, photos, completion and check out.</p>
        {rows === null && <div className="mt-6 text-sm text-[#252A34]/60">Loading…</div>}
        {rows && !rows.length && <div className="mt-6 rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-[#252A34]/60" data-testid="active-jobs-empty">No local jobs scheduled for today or in progress.</div>}
        {live.length > 0 && <Section title="In progress" rows={live} />}
        {upcoming.length > 0 && <Section title="Upcoming" rows={upcoming} />}
      </div>
    </DashboardShell>
  );
}

function Section({ title, rows }) {
  return (
    <div className="mt-6">
      <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#252A34]/60 mb-2">{title}</div>
      <div className="space-y-3">
        {rows.map((r) => (
          <Link key={r.id} to={`/dashboard/jobs/${r.id}`} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 hover:border-[#165a71] transition-colors" data-testid={`active-job-row-${r.id}`}>
            <div className="h-12 w-12 rounded-2xl bg-[#071b22] text-white flex items-center justify-center"><Smartphone className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-[#071b22] truncate">{r.service_title}</div>
              <div className="text-xs text-[#252A34]/70 flex flex-wrap gap-x-3 mt-0.5">
                <span>{r.customer_name}</span>
                <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDateTime(r.scheduled_start)}</span>
                {r.address_snapshot?.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {r.address_snapshot.line1 ? `${r.address_snapshot.line1}, ` : ""}{r.address_snapshot.city}</span>}
              </div>
              <div className="mt-1 text-xs text-[#165a71] font-medium">{r.visit_status ? VISIT_STATUS_LABEL[r.visit_status] : "Not started"}{r.summary?.working_seconds ? ` · ${fmtHM(r.summary.working_seconds)} worked` : ""}</div>
            </div>
            <StatusPill status={r.status} />
            <ChevronRight className="h-4 w-4 text-[#252A34]/40" />
          </Link>
        ))}
      </div>
    </div>
  );
}
